import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sun, Moon, HardHat, LogIn, AlertOctagon, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";

export default function Login() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email.trim().toLowerCase(), pw);
      toast.success(`Welcome, ${user.name}`);
      if (user.role === "owner") navigate("/owner");
      else navigate(user.company_id ? "/dashboard" : "/choose");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <div className="flex items-center justify-between px-4 md:px-10 py-4 border-b-2 border-foreground dark:border-safety-yellow">
        <Link to="/" className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center h-11 w-11 bg-safety-yellow text-black border-2 border-black dark:border-safety-yellow">
            <HardHat className="w-6 h-6" strokeWidth={3} />
          </span>
          <div className="leading-none">
            <div className="font-heading text-2xl md:text-3xl">KARM<span className="text-safety-yellow">.</span>SAFETY</div>
            <div className="text-[10px] md:text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mt-1">Certification Tracker v1</div>
          </div>
        </Link>
        <button data-testid="theme-toggle-login" onClick={toggle}
          className="h-11 w-11 inline-flex items-center justify-center border-2 border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black transition-colors"
          aria-label="Toggle theme">
          {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
      </div>

      <main className="flex-1 grid-bg">
        <div className="max-w-6xl mx-auto px-4 md:px-10 py-8 md:py-12 grid lg:grid-cols-12 gap-8 lg:gap-14">
          {/* Left — marketing */}
          <section className="hidden lg:flex lg:col-span-7 flex-col justify-center">
            <div className="inline-flex items-center gap-2 w-fit border-2 border-safety-yellow bg-safety-yellow/10 px-3 py-1.5 mb-6 font-bold uppercase tracking-[0.22em] text-xs">
              <span className="w-2.5 h-2.5 bg-safety-yellow animate-pulseYellow" />
              Safety. Visible. Verified.
            </div>
            <h1 className="font-heading text-[clamp(2.8rem,6vw,5.5rem)] leading-[0.9]">
              YOUR CREW.<br />
              <span className="inline-block bg-safety-yellow text-black px-3 -skew-x-3">YOUR CERTS.</span><br />
              ONE TAP.
            </h1>
            <p className="mt-8 max-w-xl text-base text-muted-foreground leading-relaxed">
              Sign in to manage your personal certifications or link to a company to track an entire crew.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3 max-w-xl">
              {[
                { Icon: CheckCircle2, label: "Current", cls: "bg-status-current text-white" },
                { Icon: AlertTriangle, label: "Expiring", cls: "bg-status-expiring text-black" },
                { Icon: AlertOctagon, label: "Expired", cls: "bg-status-expired text-white" },
              ].map((r) => (
                <div key={r.label} className={`flex items-center gap-2 px-3 h-12 font-black uppercase tracking-wider text-xs border-2 border-black ${r.cls}`}>
                  <r.Icon className="w-4 h-4" strokeWidth={3} /> {r.label}
                </div>
              ))}
            </div>
          </section>

          {/* Right — login form */}
          <section className="lg:col-span-5 flex items-center justify-center">
            <div className="brutal-card p-6 md:p-8 w-full max-w-md">
              <div className="font-heading text-3xl md:text-4xl">SIGN IN</div>
              <p className="text-sm text-muted-foreground mt-2 mb-6">Access your certifications.</p>

              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label htmlFor="login-email" className="block font-bold uppercase text-xs tracking-wider mb-2">Email</label>
                  <input id="login-email" data-testid="login-email" type="email" required
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="industrial-input" placeholder="you@company.com" autoComplete="email" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="login-password" className="block font-bold uppercase text-xs tracking-wider">Password</label>
                    <Link
                      to="/forgot-password"
                      data-testid="goto-forgot-password"
                      className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground hover:text-safety-yellow"
                    >
                      Forgot?
                    </Link>
                  </div>
                  <input id="login-password" data-testid="login-password" type="password" required
                    value={pw} onChange={(e) => setPw(e.target.value)}
                    className="industrial-input" placeholder="••••••••" autoComplete="current-password" />
                </div>
                <button type="submit" data-testid="login-submit" disabled={loading} className="btn-primary w-full">
                  {loading ? "Signing in..." : (<><LogIn className="w-5 h-5 mr-2" strokeWidth={3} />Sign In</>)}
                </button>
              </form>

              <div className="mt-6 flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">New here?</span>
                <Link to="/signup" data-testid="goto-signup"
                  className="font-bold uppercase tracking-wider underline decoration-safety-yellow decoration-4 underline-offset-4 hover:text-safety-yellow">
                  Create an account &rarr;
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>

      <footer className="border-t-2 border-foreground dark:border-safety-yellow py-4 px-6 text-center text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
        <div>© {new Date().getFullYear()} KARM LLC. OSHA-aligned certification tracking.</div>
        <div className="mt-2 flex justify-center gap-4">
          <Link to="/privacy" data-testid="footer-privacy" className="hover:text-safety-yellow">Privacy</Link>
          <span className="opacity-50">·</span>
          <Link to="/terms" data-testid="footer-terms" className="hover:text-safety-yellow">Terms</Link>
          <span className="opacity-50">·</span>
          <a href="mailto:shane@karmsafety.com" className="hover:text-safety-yellow">Contact</a>
        </div>
      </footer>
    </div>
  );
}
